import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Notifications from '../../services/notifications';
import { compose, newFormatPrice } from '../../services/utilityFunctions';
import translate from '../translate';
import withCSRF from '../withCSRF';

function CareerCappingWidget({ t, _csrf }) {
  const [summaryData, setSummaryData] = useState({
    period: 'monthly',
    capping: null,
    binaryCommissionPercentage: null,
    currentBinaryCommission: null,
    isShown: false,
  });

  const handleGetGlobalSummary = useCallback(async () => {
    try {
      const { data: response } = await axios.get(
        '/api/analytics/market-place/commissions/capping/summary',
        {
          params: {},
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );

      setSummaryData({
        ...response.data,
        isShown: true,
      });
    } catch (err) {
      console.error(err);
      Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
    }
  }, [_csrf]);

  useEffect(() => {
    handleGetGlobalSummary();
  }, []);

  const currentBinaryCommission = useMemo(
    () =>
      summaryData?.currentBinaryCommission
        ? `${newFormatPrice(
            summaryData?.currentBinaryCommission,
            true,
            summaryData?.currencyInfo?.code,
          )}`
        : '-',
    [summaryData?.currencyInfo?.code],
  );

  const capping = useMemo(
    () =>
      summaryData?.capping
        ? `${newFormatPrice(
            summaryData?.capping,
            true,
            summaryData?.currencyInfo?.code,
          )}`
        : '-',
    [summaryData?.currencyInfo?.code],
  );

  const rawProgress = useMemo(
    () =>
      Math.max(
        ((summaryData?.currentBinaryCommission || 0) /
          (summaryData?.capping || 1)) *
          100,
        0,
      ),
    [summaryData?.currentBinaryCommission, summaryData?.capping],
  );

  const progress = useMemo(
    () => parseFloat(rawProgress.toFixed(2)),
    [rawProgress],
  );

  if (!summaryData?.isShown) {
    return null;
  }

  return (
    <div className="widget-career-capping">
      <div className="card card-light-gray weekly-cap-card">
        <div className="card-body">
          <div
            className="d-flex justify-content-center flex-column align-items-center mb-2"
            style={{ gap: '5px' }}
          >
            <div className="d-flex" style={{ gap: '1rem' }}>
              <div className="small-image-container">
                <img
                  src="/images/reports/calendar.svg"
                  alt="active"
                  className="summary-image"
                />
              </div>
              <span className="value-label weekly-capping">
                {t('report.neofin.weeklyCapping')}
              </span>
            </div>

            <p style={{ marginBottom: '5px' }}>
              {t('report.neofin.binaryCommission', currentBinaryCommission)}
            </p>

            <p style={{ marginBottom: '5px' }}>
              {t('report.neofin.monthlyCapping', capping)}
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              width: '100%',
            }}
          >
            <div
              className="progress"
              style={{
                width: '100%',
                height: '20px',
                borderRadius: '15px',
                position: 'relative',
                marginBottom: 0,
                flex: 1,
              }}
            >
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${progress}%`,
                }}
              />
            </div>
            <div
              style={{
                color: 'black',
                top: '13px',
                left: '10px',
                fontSize: '1.2rem',
                margin: 0,
              }}
            >
              {progress}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

CareerCappingWidget.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default compose(translate, withCSRF)(CareerCappingWidget);
